import { DevicePlatform, NotificationAction } from '@mondaygpt/constants'
import { getToken, isSupported, onMessage } from 'firebase/messaging'
import { PushNotificationsService } from 'openapi'
import { NavigationService } from 'services'
import { firebaseConfig, getMessagingInstance } from './firebase-config'

// Cache the token to avoid unnecessary token generation requests
let cachedToken: string | null = null

export class PushNotificationService {
  private static instance: PushNotificationService

  /**
   * Singleton access to the PushNotificationService
   */
  public static getInstance(): PushNotificationService {
    if (!PushNotificationService.instance) {
      PushNotificationService.instance = new PushNotificationService()
    }
    return PushNotificationService.instance
  }

  /**
   * Initializes push notifications for the current user
   */
  public async initialize(): Promise<void> {
    try {
      if (!(await isSupported())) {
        console.warn('Firebase messaging is not supported in this browser')
        return
      }

      // Set up the notification listener
      this.setupNotificationListener()

      // Register the service worker for background notifications
      await this.registerServiceWorker()

      // Request permission and register device token
      await this.requestPermissionAndRegisterToken()
    } catch (error) {
      console.error('Error initializing push notifications:', error)
    }
  }

  /**
   * Requests notification permission and registers the device token
   */
  public async requestPermissionAndRegisterToken(): Promise<string | null> {
    try {
      // Check if notifications are supported
      if (!(await isSupported())) {
        return null
      }

      // Check if permission is already granted
      const permission = await Notification.requestPermission()

      if (permission !== 'granted') {
        console.warn('Notification permission not granted')
        return null
      }

      // Get token from Firebase
      const token = await this.getFCMToken()

      if (token) {
        await this.registerDeviceToken(token)
      }

      return token
    } catch (error) {
      console.error('Error requesting permission:', error)
      return null
    }
  }

  /**
   * Gets the FCM token, generating a new one if needed
   */
  private async getFCMToken(): Promise<string | null> {
    try {
      if (cachedToken) {
        return cachedToken
      }

      const messaging = getMessagingInstance()
      if (!messaging) {
        console.warn('Messaging instance not available')
        return null
      }

      // Get registration token
      const token = await getToken(messaging)

      if (token) {
        cachedToken = token
        return token
      } else {
        console.warn('No registration token available')
        return null
      }
    } catch (error) {
      console.error('Error getting FCM token:', error)
      return null
    }
  }

  /**
   * Registers the device token with the server
   */
  private async registerDeviceToken(token: string): Promise<void> {
    try {
      await PushNotificationsService.deviceTokenControllerCreateOrUpdateV1({
        deviceToken: token,
        platform: DevicePlatform.WEB,
      })
      console.debug('Device token registered successfully')
    } catch (error) {
      console.error('Error registering device token:', error)
    }
  }

  /**
   * Sets up the notification listener for foreground messages
   */
  private setupNotificationListener(): void {
    const messaging = getMessagingInstance()
    if (!messaging) {
      console.warn('Cannot setup notification listener: messaging instance not available')
      return
    }

    onMessage(messaging, payload => {
      console.log('Message received in foreground:', payload)

      // Display a notification for foreground messages
      if (payload.notification) {
        const { title, body } = payload.notification
        const data = payload.data || {}

        // Create and display a notification
        this.showNotification(title || 'New Notification', body || '', data)
      }
    })
  }

  /**
   * Displays a browser notification
   */
  private showNotification(title: string, body: string, data: Record<string, string> = {}): void {
    // Check if notifications are supported and permission is granted
    if (!('Notification' in window)) {
      console.warn('Notifications are not supported in this browser')
      return
    }

    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/notification-icon.png',
        data,
      })

      // Handle notification click to navigate to the chat
      notification.onclick = () => {
        window.focus()
        notification.close()

        // Extract chat navigation details from the notification data
        this.handleNotificationClick(data)
      }
    } else {
      console.warn('Notification permission not granted')
    }
  }

  /**
   * Handles notification click by navigating to the appropriate URL
   */
  private handleNotificationClick(data: Record<string, unknown>): void {
    try {
      // Check for action data
      if (data.action) {
        const actionData = typeof data.action === 'string' ? JSON.parse(data.action) : data.action

        if (actionData.type === NotificationAction.OpenURL && actionData.content?.url) {
          const url = actionData.content.url

          // Navigate using the navigation service for smoother transitions
          NavigationService.getInstance().navigate(url)
          return
        }
      }

      // Fallback for direct chatId in the data
      if (data.chatId) {
        let url = `/chats/${data.chatId}`

        if (data.messageId) {
          url += `?scrollTo=${data.messageId}`
        }

        NavigationService.getInstance().navigate(url)
      }
    } catch (error) {
      console.error('Error processing notification click:', error)
    }
  }

  /**
   * Registers the service worker for background notifications
   */
  private async registerServiceWorker(): Promise<void> {
    try {
      if ('serviceWorker' in navigator) {
        // Create URL with Firebase config as parameters
        const params = new URLSearchParams()
        Object.entries(firebaseConfig).forEach(([key, value]) => {
          if (value) params.append(key, value as string)
        })

        const swUrl = `/firebase-messaging-sw.js?${params.toString()}`

        // Register the service worker
        const registration = await navigator.serviceWorker.register(swUrl, {
          scope: '/',
        })

        console.log('Service worker registered successfully:', registration)
      }
    } catch (error) {
      console.error('Service worker registration failed:', error)
    }
  }

  /**
   * Unregisters the device token when user logs out
   */
  public async unregisterToken(): Promise<void> {
    try {
      const token = cachedToken

      if (!token) {
        return
      }

      await PushNotificationsService.deviceTokenControllerDeleteV1(token)

      cachedToken = null
      console.log('Device token unregistered successfully')
    } catch (error) {
      console.error('Error unregistering device token:', error)
    }
  }
}
